import FullCalendar from '@fullcalendar/react';
import rrulePlugin from '@fullcalendar/rrule';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dimmer, Image, Input, Loader, Modal } from 'semantic-ui-react';
import moment from 'moment-timezone';
import UserContext from '../../../contexts/UserContext';
import sendToast from '../../../utils/Toast';
import { fetchAllCommunityMenuItems } from '../../services/Menu';
import { MenuItem } from '../../types/Menu';
import { getStandaloneSignageImage, getServiceInstanceImage } from '../../services/ImageUploader';
import { customDateTimeFormat, capitalizeFirstLetter } from '../../../utils';
import '../../pages/calendar/style.less';
import '../../pages/menu/style.less';

interface Props {
    setLoadingMenu: React.Dispatch<React.SetStateAction<boolean>>;
    facilityTimezone: string;
}

const StandaloneMenu = ({ setLoadingMenu, facilityTimezone }: Props) => {
    const calendarRef = useRef<FullCalendar>(null);
    const [selectedDateRange, setSelectedDateRange] = useState({
        startDateTimeString: moment().startOf('month').format(customDateTimeFormat),
        endDateTimeString: moment().endOf('month').format(customDateTimeFormat),
    });
    const [selectedMenuItem, setSelectedMenuItem] = useState<MenuItem>();
    const [menuItems, setMenuItems] = useState<Array<MenuItem>>([]);
    const [showMenuModal, setShowMenuModal] = useState<boolean>(false);
    const [imageLoading, setImageLoading] = useState<boolean>(false);
    const [imageUrl, setImageUrl] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState<string>('');

    const {
        userObject: {
            registrants: { list: listOfResidents, activeRegistrantsIndexes },
        },
    } = useContext(UserContext);
    const correspondingSpeak2Resident = listOfResidents[activeRegistrantsIndexes[0]];

    // Dispatch initial month to header on mount
    useEffect(() => {
        const timer = setTimeout(() => {
            window.dispatchEvent(new CustomEvent('calendarMonthChange', { detail: moment().format('MMMM') }));
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        fetchMenuItems();
    }, [selectedDateRange.startDateTimeString, selectedDateRange.endDateTimeString, facilityTimezone]);

    const fetchMenuItems = async () => {
        try {
            setLoadingMenu(true);
            if (selectedDateRange.startDateTimeString && selectedDateRange.endDateTimeString && facilityTimezone) {
                const startDateTimeString = moment
                    .tz(selectedDateRange.startDateTimeString, facilityTimezone)
                    .format(customDateTimeFormat);
                const endDateTimeString = moment.tz(selectedDateRange.endDateTimeString, facilityTimezone).format(customDateTimeFormat);
                const menuResp = await fetchAllCommunityMenuItems({
                    filter: {
                        Facility: correspondingSpeak2Resident.Facility,
                        startDateTimeString: startDateTimeString,
                        endDateTimeString: endDateTimeString,
                        ...(correspondingSpeak2Resident.Unit?.RoomCategory && {
                            calendarType: [correspondingSpeak2Resident.Unit.RoomCategory],
                        }),
                    }
                });
                setMenuItems(menuResp);
            }
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Failed to fetch menu items');
        } finally {
            setLoadingMenu(false);
        }
    };

    const handleMonthChange = ({ start, end }) => {
        const startDateTimeString = moment(start).format(customDateTimeFormat);
        const endDateTimeString = moment(end).format(customDateTimeFormat);
        setSelectedDateRange({
            startDateTimeString,
            endDateTimeString,
        });
        if (start && end) {
            const mid = new Date((new Date(start).getTime() + new Date(end).getTime()) / 2);
            const monthName = moment(mid).format('MMMM');
            window.dispatchEvent(new CustomEvent('calendarMonthChange', { detail: monthName }));
        }
    };

    const handlePrev = () => calendarRef.current?.getApi().prev();
    const handleNext = () => calendarRef.current?.getApi().next();
    const handleToday = () => calendarRef.current?.getApi().today();

    const handleEventClick = async (info) => {
        const selectedMenuId = info.event._def.publicId;
        const selectedMenu = menuItems.find((menu) => String(menu._id) === String(selectedMenuId));
        if (selectedMenu && selectedMenu.svcMenuInstance && selectedMenu.startDate) {
            const dateString = moment(selectedMenu.startDate, facilityTimezone).format('ddd MMM DD YYYY');
            selectedMenu.dateString = dateString;
        }
        setSelectedMenuItem(selectedMenu);
        setShowMenuModal(true);
        try {
            setImageLoading(true);
            let image: string = '';
            if (selectedMenu && selectedMenu.svcMenuInstance && selectedMenu.image) {
                image = await getServiceInstanceImage({
                    facilityId: selectedMenu.Facility,
                    svcInstanceId: String(selectedMenu.srcId || selectedMenu._id),
                    fileName: selectedMenu.image,
                });
            } else if (selectedMenu && selectedMenu.signageImageId) {
                image = await getStandaloneSignageImage({
                    facilityId: selectedMenu.Facility,
                    contentType: "menus",
                    contentId: selectedMenu.signageImageId,
                });
            } else if (selectedMenu) {
                const category = selectedMenu.svcMenuInstance ? selectedMenu.menuCategory : selectedMenu.category;
                image = `https://speak2-signage.s3.amazonaws.com/menus/default-images/${category}.png`;
            }
            setImageUrl(image);
        } catch (error) {
            sendToast('warn', error instanceof Error ? error.message : 'Failed to fetch image');
        } finally {
            setImageLoading(false);
        }
    };

    const getCategory = (item: MenuItem) => {
        return item.svcMenuInstance ? item.menuCategory : item.category;
    };

    const getMenuDisplayText = useCallback((item: MenuItem) => {
        return item.alexaReadableString
            || (item.text ? item.text.replace(/(<([^>]+)>)/gi, '') : '')
            || (item.name ? item.name.replace(/(<([^>]+)>)/gi, '') : '');
    }, []);

    const filteredMenuItems = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        if (!query) return menuItems;
        return menuItems.filter((item) => getMenuDisplayText(item).toLowerCase().includes(query));
    }, [menuItems, searchQuery, getMenuDisplayText]);

    const getMenuDate = (item: MenuItem) => {
        if (item.startDate) {
            return moment.tz(item.startDate, facilityTimezone);
        }
        return moment(item.dateString);
    };

    const closeModal = () => {
        setShowMenuModal(false);
        setImageUrl('');
    };

    return (
        <div className="menu-standalone-view">
            {/* Toolbar: filter buttons + nav - right-aligned to match Figma */}
            <div className="calendarToolbar">
                <div className="calendarToolbarRight">
                    <div className="calendarSearchBox">
                        <Input
                            icon="search"
                            placeholder="Search meals..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            size="small"
                        />
                    </div>
                    <div className="calendarNavButtons">
                        <button className="calendarNavBtn" onClick={handleToday}>Today</button>
                        <div className="calendarNavGroup">
                            <button className="calendarNavBtn calendarNavBtn--arrow" onClick={handlePrev}>&#8249;</button>
                            <button className="calendarNavBtn calendarNavBtn--arrow" onClick={handleNext}>&#8250;</button>
                        </div>
                    </div>
                </div>
            </div>

            <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, interactionPlugin, rrulePlugin]}
                initialView="dayGridMonth"
                headerToolbar={false}
                contentHeight="auto"
                dayHeaderFormat={{ weekday: 'long' }}
                dayMaxEvents={false}
                showNonCurrentDates={false}
                events={[
                    ...filteredMenuItems.map((event) => {
                        const timeSlots = {
                            breakfast: 8,
                            lunch: 12,
                            dinner: 20,
                            snack: 18,
                            alternative: 22,
                        };
                        const category = getCategory(event);
                        return {
                            title: getMenuDisplayText(event),
                            start: moment.tz(event.startDate, facilityTimezone).startOf('day').unix() * 1000 + timeSlots[category] * 60,
                            id: event._id,
                            backgroundColor: 'transparent',
                            borderColor: 'transparent',
                            textColor: '#333',
                            classNames: event.audioId ? [`category-${category}`, "audio"] : [`category-${category}`],
                            extendedProps: {
                                category: category,
                                menuText: getMenuDisplayText(event),
                            },
                        };
                    }),
                ]}
                datesSet={handleMonthChange}
                eventClick={handleEventClick}
                eventContent={(info) => {
                    const category = info.event.extendedProps?.category || '';
                    const menuText = info.event.extendedProps?.menuText || info.event.title || '';
                    return (
                        <div className="menu-event-content">
                            <span className="menu-event-category">
                                {capitalizeFirstLetter(category)}
                            </span>
                            {menuText && (
                                <>
                                    <span> – </span>
                                    <span>{menuText}</span>
                                </>
                            )}
                        </div>
                    );
                }}
            />

            {/* Modal for displaying menu details */}
            <Modal
                open={showMenuModal}
                onClose={closeModal}
                size="small"
                className="menuDetailModal"
            >
                <Dimmer active={imageLoading} inverted>
                    <Loader content="Loading..." />
                </Dimmer>
                {selectedMenuItem && (
                    <Modal.Content style={{ padding: 0 }}>
                        <div className="menuModalRow">
                            {imageUrl && (
                                <div className="menuModalImageWrapper">
                                    <Image src={imageUrl} className="menuModalImage" />
                                </div>
                            )}
                            <div className="menuModalInfo">
                                <h2 className="menuModalTitle">
                                    {capitalizeFirstLetter(getCategory(selectedMenuItem))} on {getMenuDate(selectedMenuItem).format('dddd')}
                                </h2>
                                <p className="menuModalDate">
                                    {getMenuDate(selectedMenuItem).format('dddd, MMMM D, YYYY')}
                                </p>
                                <p className="menuModalText">
                                    {getMenuDisplayText(selectedMenuItem)}
                                </p>
                            </div>
                        </div>
                    </Modal.Content>
                )}
                <Modal.Actions>
                    <Button color="red" onClick={closeModal}>
                        Close
                    </Button>
                </Modal.Actions>
            </Modal>
        </div>
    );
};

export default StandaloneMenu;
